import { useState } from "react";
import { toast } from "sonner";
import { Linkedin, Send, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.message.trim()) e.message = "Please write a short message";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (event) => {
    setForm({ ...form, [k]: event.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      };
      const { data } = await api.post("/contact", payload);
      toast.success("Message received", {
        description: data?.email_sent
          ? "Thanks — I've received your note and will be in touch soon."
          : "Thanks — I've received your note and will be in touch soon.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Could not send message", {
        description:
          err?.response?.data?.detail ||
          "Please try again in a moment, or reach out via LinkedIn.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Copy */}
        <div className="lg:col-span-5">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-accent">
            // Contact
          </p>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Let's talk.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-md">
            Enterprise growth, AI enablement, leadership development, or a
            question about Corporate Athlete 2.0 — I'd love to hear from you.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-linkedin-link"
              href="https://www.linkedin.com/in/vicente-palomarez-b89290109/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-4 border border-border bg-background hover:-translate-y-1 transition-transform group"
            >
              <div className="h-10 w-10 border border-border grid place-items-center bg-secondary/60">
                <Linkedin className="h-4 w-4 text-accent" />
              </div>
              <div>
                <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                  LinkedIn
                </p>
                <p className="text-sm font-medium mt-1 group-hover:underline">
                  Connect with me
                </p>
              </div>
            </a>

            <div className="flex items-start gap-4 p-4 border border-border bg-background">
              <div className="h-10 w-10 border border-border grid place-items-center bg-secondary/60">
                <MapPin className="h-4 w-4 text-ai" />
              </div>
              <div>
                <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                  Availability
                </p>
                <p className="text-sm font-medium mt-1">
                  Selective advisory & speaking engagements
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <div className="relative border border-border bg-background">
            <div className="hairline-ai" />
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="p-6 sm:p-8 lg:p-10 space-y-6"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="font-mono-ui text-[10px] uppercase tracking-widest">
                    Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Your name"
                    className="mt-2 rounded-sm"
                    aria-invalid={!!errors.name}
                  />
                  {errors.name && (
                    <p data-testid="contact-name-error" className="text-xs text-destructive mt-1.5">{errors.name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email" className="font-mono-ui text-[10px] uppercase tracking-widest">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="you@company.com"
                    className="mt-2 rounded-sm"
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && (
                    <p data-testid="contact-email-error" className="text-xs text-destructive mt-1.5">{errors.email}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="subject" className="font-mono-ui text-[10px] uppercase tracking-widest">
                  Subject <span className="text-muted-foreground normal-case">(optional)</span>
                </Label>
                <Input
                  id="subject"
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={onChange("subject")}
                  placeholder="What's this about?"
                  className="mt-2 rounded-sm"
                />
              </div>

              <div>
                <Label htmlFor="message" className="font-mono-ui text-[10px] uppercase tracking-widest">
                  Message
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Tell me a bit about what you're working on…"
                  className="mt-2 rounded-sm min-h-[160px]"
                  aria-invalid={!!errors.message}
                />
                {errors.message && (
                  <p data-testid="contact-message-error" className="text-xs text-destructive mt-1.5">{errors.message}</p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-muted-foreground">
                  By sending, you agree to be contacted at the email provided.
                </p>
                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  size="lg"
                  className="rounded-sm gap-2 w-full sm:w-auto"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send message
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
